import { compact } from 'lodash';
import { EventSearchForm } from '@mark43/cad-api';
import { formatMonthDayHoursMinutes } from '../common/helpers/utils';
import {
    formatBoolean,
    formatLocations,
    formatOption,
    formatOptions,
    formatPersons,
    formatString,
    formatStrings,
    formatVehicles,
} from '../search-windows/form-value-formatters';

export function formatEventSearchForm(
    eventSearchForm: EventSearchForm,
    renDisplayName: string,
    subpremiseSupport: boolean
) {
    const eventInformationSummary = compact([
        formatStrings(
            'Event #s',
            eventSearchForm.eventNumbers.map((eventNumber) => eventNumber.eventNumber)
        ),
        formatStrings(
            `${renDisplayName}s`,
            eventSearchForm.reportingEventNumbers.map((ren) => ren.reportingEventNumber)
        ),
        formatOption('Dispatched', eventSearchForm.dispatched),
        formatOption(`${renDisplayName} Generated`, eventSearchForm.reportingEventNumberGenerated),
        formatOptions('Call For Service', eventSearchForm.callForServices),
        formatOptions('Labels', eventSearchForm.eventLabels),
        formatOptions('Priority Level', eventSearchForm.priorities),
        formatOptions('Agency', eventSearchForm.agencyProfiles),
        formatOptions('Assisting Agencies', eventSearchForm.assistingAgencyProfiles),
        formatOption('Agency Type', eventSearchForm.agencyType),
        formatOptions('Dispatch Area', eventSearchForm.dispatchAreas),
        formatOptions('Radio Channels', eventSearchForm.radioChannels),
        formatOptions('Hospitals', eventSearchForm.hospitals),
        formatOptions('Call Origin', eventSearchForm.eventOrigins),
        formatOption('Call Type', eventSearchForm.eventQueue),
        formatOptions('Dispatcher', eventSearchForm.dispatchers),
        formatOptions('Call Taker', eventSearchForm.callTakers),
        formatString('Narrative Keywords', eventSearchForm.narrativeKeywords),
        formatOption('Time Range', eventSearchForm.timeRangeOption),
        formatString(
            'Date Range Start',
            eventSearchForm.dateRange?.start
                ? formatMonthDayHoursMinutes(eventSearchForm.dateRange.start)
                : undefined
        ),
        formatString(
            'Date Range End',
            eventSearchForm.dateRange?.end
                ? formatMonthDayHoursMinutes(eventSearchForm.dateRange.end)
                : undefined
        ),
        formatOptions('Primary Disposition', eventSearchForm.primaryDispositions),
        formatOptions('Secondary Dispositions', eventSearchForm.secondaryDispositions),
        formatString('Self Initiated', eventSearchForm.selfInitiated),
        formatOptions('Primary Unit', eventSearchForm.primaryUnits),
        formatOptions('Involved Units', eventSearchForm.involvedUnits),
        formatOptions('Primary Officer', eventSearchForm.primaryOfficers),
        formatOptions('Involved Officers', eventSearchForm.involvedOfficers),
    ]).join(', ');
    const eventLocationSummary = compact([
        formatLocations(eventSearchForm.locations, subpremiseSupport),
        formatString('Verification', eventSearchForm.locationVerification),
        formatStrings(
            'Polygon',
            eventSearchForm.polygonPoints.map(({ latitude, longitude }) =>
                latitude !== undefined && longitude !== undefined
                    ? `${latitude}/${longitude}`
                    : undefined
            )
        ),
    ]).join(', ');
    const personSummaries = formatPersons(eventSearchForm.persons);
    const vehicleSummaries = formatVehicles(eventSearchForm.vehicles);
    const otherSummary = compact([
        formatOption('Has attachment(s)', eventSearchForm.hasAttachments),
        formatString('File name', eventSearchForm.attachmentFileName),
        formatOptions('File types', eventSearchForm.attachmentCategories),
        formatString('File description', eventSearchForm.attachmentDescription),
        formatBoolean('Include Active Events', eventSearchForm.includeActiveEvents),
        formatBoolean('Enable Fuzzy Matching', eventSearchForm.fuzzyMatchingEnabled),
    ]).join(', ');
    return compact([
        eventInformationSummary,
        eventLocationSummary,
        personSummaries ? `Persons:  ${personSummaries}` : undefined,
        vehicleSummaries ? `Vehicles: ${vehicleSummaries}` : undefined,
        otherSummary,
    ]).join('; ');
}
